import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Row, Col, Input, DatePicker } from 'antd';
const { Search } = Input;
const { RangePicker } = DatePicker;
import FileLoaderService, { getFileStatusText } from '../services/file-loader-service';
import FileTotalCount from './shared/file-total-count';
import dayjs from 'dayjs';
import RosterService from '../services/roster-service';
import { formatDateLong } from '../utils/format';

const DATE_RANGES = {
    'Last Month': [ dayjs().startOf( 'month' ), dayjs().endOf( 'month' ) ],
    'Last 3 Months': [ dayjs().startOf( 'month' ).subtract( 3, 'month' ), dayjs().endOf( 'month' ) ],
    'Last 6 Months': [ dayjs().startOf( 'month' ).subtract( 6, 'month' ), dayjs().endOf( 'month' ) ]
};

const DATE_RANGES_FOR_PICKER = Object.entries( DATE_RANGES ).map( ( [ label, dates ] ) => ( { label, value: dates } ) );
const DEFAULT_PAGE_SIZE = 25;

const getSorter = ( column, type ) => {
    return ( a, b ) => {
        let valA = typeof column === 'function' ? column( a ) : a[ column ];
        let valB = typeof column === 'function' ? column( b ) : b[ column ];

        if ( type === 'number' ) { return parseFloat( valA ) - parseFloat( valB ); }
        if ( type === 'date' ) { return new Date( valA ).valueOf() - new Date( valB ).valueOf(); }
        return valA.localeCompare( valB, undefined, { sensitivity: 'base' } );
    };
};

const FileTable = ( props ) => {
    const {
        pageSize = DEFAULT_PAGE_SIZE,
        fileTypes = [],
        onFileClick,
        auth
    } = props;

    const [ data, setData ] = useState( null );
    const [ loading, setLoading ] = useState( false );
    const [ fileNameFilter, setFileNameFilter ] = useState( '' );
    const [ dateFilter, setDateFilter ] = useState( DATE_RANGES[ 'Last 3 Months' ] );

    const loadFiles = async () => {
        const searchFilters = {};
        if ( fileNameFilter ) {
            searchFilters.fileName = fileNameFilter;
        }
        if ( Array.isArray( fileTypes ) && fileTypes.length ) {
            searchFilters.fileTypeName = fileTypes.map( type => type.value );
        }
        if ( dateFilter && dateFilter[ 0 ] ) {
            searchFilters.startDate = dateFilter[ 0 ].toDate();
        }
        if ( dateFilter && dateFilter[ 1 ] ) {
            searchFilters.endDate = dateFilter[ 1 ].toDate();
        }

        setLoading( true );
        try {
            const fileLoader = FileLoaderService( auth.state );
            const roster = RosterService( auth.state.original );
            // fix getting all the files so we can get all the names
            const data = await fileLoader.searchFiles( 5000, 0, searchFilters );
            // get all the persons for the files
            const personMap = await roster.getPersonMap(
                data.files.map( f => f.createdByPersonId )
            );
            // create a createdByPerson prop on the file
            data.files.forEach( file => {
                file.createdByPerson = personMap[ file.createdByPersonId ];
            } );

            setData( data.files );
        } catch ( error ) {
            console.error( 'Error fetching files', error.status );
            setData( null );
        } finally {
            setLoading( false );
        }
    };

    useEffect( () => {
        loadFiles();
    }, [ dateFilter, pageSize, auth?.state?.claims?.company_id ] );

    const handleFileClick = useCallback( ( fileId ) => ( e ) => {
        e.preventDefault();
        e.stopPropagation();

        if ( typeof onFileClick === 'function' ) {
            onFileClick( fileId );
        }
    }, [ onFileClick ] );

    const columns = useMemo( () => [
        {
            title: 'File Name',
            key: 'name',
            dataIndex: 'name',
            render: ( fileName, { id } ) => (
                <a href={ `#${ id }` } onClick={ handleFileClick( id ) }>
                    <span>{ fileName }</span>
                </a>
            ),
            sorter: getSorter( 'name', 'string' )
        },
        {
            title: 'File Type',
            key: 'initialFileType',
            dataIndex: 'initialFileType',
            render: ( fileType ) => {
                const match = fileTypes.find( t => t.value.toLowerCase() === fileType.toLowerCase() );
                if ( match ) { return match.text; }
                return fileType;
            },
            sorter: getSorter( 'initialFileType', 'string' )
        },
        {
            title: 'File Status',
            key: 'status',
            dataIndex: 'status',
            render: ( fileStatus, file ) => getFileStatusText( file ),
            sorter: getSorter( getFileStatusText, 'string' ),
            filters: [
                'Uploading',
                'Validating',
                'Validated',
                'Needs Re-Validation',
                'Validation Failed',
                'Validated with Errors',
                'Loading',
                'Loaded',
                'Loading Skipped',
                'Loading Failed',
                'Loaded with Errors'
            ].map( value => ( { value, text: value } ) ),
            filterMultiple: true,
            onFilter: ( statusText, file ) => statusText === getFileStatusText( file ),
            minWidth: 140,
        },
        {
            title: 'Records',
            key: 'totalRecords',
            dataIndex: 'id',
            render: ( id, file ) => (
                <FileTotalCount file={ file } countKey='totalFileRecordCount' className='file-loader-record-totals' />
            ),
            minWidth: 106,
            sorter: getSorter( 'totalFileRecordCount', 'number' )
        },
        {
            title: 'Errors',
            key: 'totalErrors',
            dataIndex: 'id',
            render: ( id, file ) => (
                <FileTotalCount file={ file } countKey='totalErrorCount' className='file-loader-error-totals' />
            ),
            minWidth: 100,
            sorter: getSorter( 'totalErrorCount', 'number' )
        },
        {
            title: 'Loaded On',
            key: 'lastProcessedAt',
            dataIndex: 'lastProcessedAt',
            render: ( loadDate ) => loadDate ? formatDateLong( loadDate ) : '',
            sorter: getSorter( 'lastProcessedAt', 'date' )
        },
        {
            title: 'Loaded By',
            key: 'createdByPersonId',
            dataIndex: 'createdByPerson',
            render: ( person ) => <span>{ person.givenName } { person.surname }</span>,
            sorter: ( ( a, b ) => {
                a = a.createdByPerson;
                b = b.createdByPerson;
                if ( a.surname === b.surname ) {
                    return a.givenName.localeCompare( b.givenName, undefined, { sensitivity: 'base' } );
                }
                return a.surname.localeCompare( b.surname, undefined, { sensitivity: 'base' } );
            } )
        }
    ], [ fileTypes, handleFileClick ] );

    return (
        <div className="file-loader-file-table">
            <Row className="file-loader-file-table-filter" style={ { margin: '8px 0' } } >
                <Col>
                    <Search
                        enterButton
                        placeholder="Search for filename"
                        name="fileName"
                        value={ fileNameFilter }
                        onChange={ ( e ) => setFileNameFilter( e.target.value ) }
                        onSearch={ loadFiles }
                    />
                </Col>
                <Col flex={ 1 } />
                <Col>
                    <RangePicker
                        presets={ DATE_RANGES_FOR_PICKER }
                        value={ dateFilter }
                        onChange={ ( value ) => setDateFilter( value ) }
                    />
                </Col>
            </Row>

            <Table
                dataSource={ data }
                columns={ columns }
                loading={ loading }
                pagination={ {
                    position: [ 'none', 'bottomRight' ],
                    pageSize: pageSize,
                    showSizeChanger: false,
                } }
                locale={ {
                    emptyText: 'No Files'
                } }
                scroll={ { x: 'max-content', y: false } }
                tableLayout="auto"
                rowKey={ record => record.id }
            />
        </div>
    );
};

export default FileTable;